import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "./ScheduleUpdater.module.css";
import { Search, Calendar, Plane, MapPin, Filter, RefreshCw, CheckCircle, AlertTriangle, Users } from "lucide-react";
import { useToast } from "../../../../context/ToastContext";
import {
  useGetAirlinesForScheduleUpdater,
  useGetSuppliersForScheduleUpdater,
  useSearchFlights,
  useCheckScheduleApi,
  useApplyScheduleChanges,
  useScheduleDashboard,
  useNotifyCustomer,
} from "../../../../api/products/useScheduleUpdaterApi";
import { formatDMY } from "../../../../utils/dateFormat";
import ManualScheduleChangeModal from "./ManualScheduleChangeModal";
import OperationalFlightModal from "./OperationalFlightModal";

// Rows per page. The list runs to several hundred segments on real data (434 on
// production the day this was added), and rendering them all made the screen
// slow to open and impossible to work down.
const PAGE_SIZES = [25, 50, 100, 200];

const emptyFilters = {
  dateFrom: "",
  dateTo: "",
  supplier: "all",
  airline: "all",
  flightNumber: "",
  route: "",
  bookingStatus: "all",
  bookingDateFrom: "",
  bookingDateTo: "",
  bookingNumber: "",
  pnr: ""
};

const ScheduleUpdater = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("api");
  const [selectedFlights, setSelectedFlights] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [operationalKey, setOperationalKey] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // §66 — segments chosen via "all bookings on this flight" rather than by
  // ticking rows. Held separately so the table selection is left untouched.
  const [flightWideSegments, setFlightWideSegments] = useState(null);

  // "draft" filters (what the user is editing)
  const [filters, setFilters] = useState(emptyFilters);
  // "applied" filters (what actually drives the results)
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  // API hooks
  const { data: airlines = [] } = useGetAirlinesForScheduleUpdater();
  const { data: suppliers = [] } = useGetSuppliersForScheduleUpdater();
  const { data: flights, loading: searchLoading, error: searchError, search } = useSearchFlights();
  const { execute: checkSchedule, loading: checkLoading } = useCheckScheduleApi();
  const { execute: applyChanges, loading: applyLoading } = useApplyScheduleChanges();
  const { data: counters, load: loadCounters } = useScheduleDashboard();
  const { execute: notifyCustomer, loading: notifyLoading } = useNotifyCustomer();

  // Initial load on mount
  useEffect(() => {
    search(emptyFilters);
    loadCounters();
  }, [search, loadCounters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    setSelectedFlights([]);
    search(nextFilters);
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSelectedFlights([]);
    search(emptyFilters);
  };

  // Check if any filter is active (to show visual indicator)
  const hasActiveFilters = Object.entries(appliedFilters).some(([key, val]) => {
    if (key === "supplier" || key === "airline" || key === "bookingStatus") return val !== "all";
    return val !== "";
  });

  const handleSelectFlight = (flightId) => {
    setSelectedFlights(prev =>
      prev.includes(flightId) ? prev.filter(id => id !== flightId) : [...prev, flightId]
    );
  };

  // Select-all covers the rows actually on screen — this page of this tab. It
  // used to tick every segment in the whole result including the other tab's,
  // so an operator who ticked the box on API Flights was silently also acting
  // on manual ones they had never seen.
  const handleSelectAll = () => {
    const ids = pagedFlights.map((f) => f.id);
    const allOn = ids.length > 0 && ids.every((id) => selectedFlights.includes(id));
    setSelectedFlights((prev) =>
      allOn ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]
    );
  };

  const handleCheckAPI = async () => {
    const result = await checkSchedule();
    // A check that found changes is a success, not a failure — but it must not
    // read as "all clear" either, so the counts are in the message either way.
    showToast(result.message, result.success ? "success" : "error");
    setCheckResult(result.data || null);
    search(appliedFilters);
    loadCounters();
  };

  /**
   * §47 — email the lead booker, which is the last thing a schedule change
   * needs. Once the documents are reissued AND the customer is told, the change
   * resolves (§76) and the row stops being highlighted; that is what the
   * highlight has always meant — "this still needs work".
   */
  const handleNotify = async (flight) => {
    const changeId = flight.lastChange?.id;
    if (!changeId) {
      showToast("This flight has no recorded schedule change to notify about.", "error");
      return;
    }
    if (!window.confirm(
      `Email the lead booker of ${flight.bookingNumber} about this schedule change?`
    )) return;

    const result = await notifyCustomer(changeId);
    showToast(result.message, result.success ? "success" : "error");
    if (result.success) {
      search(appliedFilters);
      loadCounters();
    }
  };

  // Opens the Manual Schedule Change form (§37) — the new schedule and the
  // mandatory reason are collected there, never assumed here.
  const handleOpenApply = () => {
    if (selectedFlights.length === 0) {
      showToast("Please select at least one flight", "error");
      return;
    }
    setScheduleModalOpen(true);
  };

  // §66 — "Find & Update All Bookings on This Flight". Bypasses the table
  // selection entirely: the flight, not the row, defines the scope.
  const handleApplyToWholeFlight = (bookings) => {
    setFlightWideSegments(bookings);
    setOperationalKey(null);
    setScheduleModalOpen(true);
  };

  const handleApplyChanges = async (newSchedule, options) => {
    const segments = applyTargets.map(f => ({
      productId: f.productId,
      flightSegmentId: f.flightSegmentId,
      productUpdatedAt: f.productUpdatedAt, // §69 race guard
    }));

    const result = await applyChanges(segments, newSchedule, options);

    // §70 — the modal keeps the per-booking breakdown on screen; a single
    // failure must never read as a clean sweep.
    showToast(result.message, result.success ? "success" : "error");
    if (result.data?.summary?.applied) {
      setSelectedFlights([]);
      search(appliedFilters);
    }
    return result;
  };

  // §8 — the tabs now mean something. API flights are the ones Airtuerk has
  // actually sent a file for, so a schedule check can verify them; manual
  // flights have no supplier file and can only be corrected by hand.
  const filteredFlights = flights.filter((f) =>
    activeTab === "api" ? f.source === "API" : f.source !== "API"
  );

  // ── Pagination ──────────────────────────────────────────────────────────
  // Over the FILTERED rows, so the count and the pages always describe the tab
  // being looked at. Page resets whenever the set underneath changes — tab,
  // filters or a new search — otherwise page 7 of the old result stays selected
  // and the table reads as empty.
  const total = filteredFlights.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pagedFlights = filteredFlights.slice(pageStart, pageStart + pageSize);
  const rangeFrom = total === 0 ? 0 : pageStart + 1;
  const rangeTo = Math.min(pageStart + pageSize, total);

  const pageNumbers = useMemo(() => {
    const nums = [];
    const win = 5;
    let s = Math.max(1, safePage - Math.floor(win / 2));
    const e = Math.min(pageCount, s + win - 1);
    s = Math.max(1, e - win + 1);
    for (let i = s; i <= e; i += 1) nums.push(i);
    return nums;
  }, [pageCount, safePage]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, appliedFilters, flights]);
  const selectedSegments = flights.filter(f => selectedFlights.includes(f.id));
  // Flight-wide scope wins when it was chosen, otherwise the ticked rows (§66).
  const applyTargets = flightWideSegments || selectedSegments;
  const changesCount = filteredFlights.filter(f => f.hasScheduleChange).length;
  const isLoading = searchLoading || checkLoading || applyLoading;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Flight Schedule Updater</h1>
          <p className={styles.subtitle}>
            Central tool for detecting, applying, and logging flight schedule changes from API and manual sources
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryBtn} onClick={handleCheckAPI} disabled={isLoading}>
            <RefreshCw size={18} />
            {checkLoading ? "Checking…" : "Check API"}
          </button>
          <button className={styles.primaryBtn} onClick={handleOpenApply} disabled={selectedFlights.length === 0 || isLoading}>
            <CheckCircle size={18} />
            {applyLoading ? "Applying…" : `Apply Changes (${selectedFlights.length})`}
          </button>
        </div>
      </div>

      {/* Counters (§62). A null counter has no subsystem behind it and renders
          as "—", never 0 — see §21. */}
      {counters && (
        <div className={styles.countersStrip}>
          {[
            { key: "upcomingSegments", label: "Upcoming segments" },
            { key: "operationalFlights", label: "Operational flights" },
            { key: "affectedBookings", label: "Bookings" },
            { key: "changesDetected", label: "Changes detected", tone: "warn" },
            { key: "awaitingReview", label: "Awaiting review", tone: "warn" },
            { key: "criticalChanges", label: "Critical", tone: "critical" },
            { key: "invalidConnections", label: "Invalid connections", tone: "critical" },
            { key: "outOfSyncBookings", label: "Out of sync", tone: "warn" },
            { key: "cancelledSegments", label: "Cancelled", tone: "critical" },
            { key: "documentsOutOfSync", label: "Documents out of sync", tone: "critical" },
            { key: "customerNotificationsPending", label: "Notifications pending", tone: "warn" },
            { key: "checkedToday", label: "Checked today" },
            { key: "apiErrors", label: "API errors" },
          ].map(({ key, label, tone }) => {
            const value = counters[key];
            const unavailable = value === null || value === undefined;
            return (
              <div
                key={key}
                className={`${styles.counterTile} ${unavailable ? styles.counterUnavailable : ""} ${
                  !unavailable && value > 0 && tone === "critical" ? styles.counterCritical : ""
                } ${!unavailable && value > 0 && tone === "warn" ? styles.counterWarn : ""}`}
                title={unavailable ? "Not available — no supplier schedule check is configured" : undefined}
              >
                <span className={styles.counterValue}>{unavailable ? "—" : value}</span>
                <span className={styles.counterLabel}>{label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "api" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("api")}
        >
          <RefreshCw size={18} />
          API Flights
          {activeTab === "api" && changesCount > 0 && (
            <span className={styles.tabBadge}>{changesCount}</span>
          )}
        </button>
        <button
          className={`${styles.tab} ${activeTab === "manual" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("manual")}
        >
          <Plane size={18} />
          Manual Flights
        </button>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>
          <AlertTriangle size={20} />
        </div>
        <div className={styles.infoText}>
          {activeTab === "api" ? (
            <>
              <strong>API Flights:</strong> flights Airtuerk has sent a file for.
              <strong> Check API</strong> fetches the latest from Airtuerk and compares it with what
              SUNSKY holds. Only these can be verified against a supplier — review and confirm before
              applying.
            </>
          ) : (
            <>
              <strong>Manual Flights:</strong> no supplier file exists for these, so a schedule check
              cannot confirm anything about them. Record changes received by email or phone using
              Manual Schedule Change. All updates are logged.
            </>
          )}
        </div>
      </div>

      {/* §16/§21 — what the last check actually covered. "36 verified" and
          "206 not checked" are different facts and are shown as such. */}
      {checkResult?.summary && (
        <div className={styles.checkResultBar}>
          <RefreshCw size={15} />
          <span>
            <strong>{checkResult.summary.checked}</strong> verified against Airtuerk ·{" "}
            <strong>{checkResult.summary.changed}</strong> changed ·{" "}
            <strong>{checkResult.summary.notFound}</strong> not checked (no supplier file)
            {checkResult.fetched?.newFiles > 0 && ` · ${checkResult.fetched.newFiles} new file(s) fetched`}
            {checkResult.fetched?.error && ` · fetch failed: ${checkResult.fetched.error}`}
          </span>
          <button className={styles.checkResultClose} onClick={() => setCheckResult(null)}>
            ×
          </button>
        </div>
      )}

      {/* Filters Section */}
      <div className={styles.filtersSection}>
        <div className={styles.filtersHeader}>
          <button className={styles.filtersToggle} onClick={() => setShowFilters(!showFilters)}>
            <Filter size={18} />
            Filters
            {hasActiveFilters && <span className={styles.activeFilterDot} />}
            {showFilters ? "▼" : "▶"}
          </button>
          <button className={styles.clearFiltersBtn} onClick={handleClearFilters}>
            Clear All
          </button>
        </div>

        {showFilters && (
          <>
            <div className={styles.filtersGrid}>
              <div className={`${styles.filterField} ${styles.dateRangeField}`}>
                <label>FLIGHT DATE RANGE</label>
                <div className={styles.dateRange}>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={e => handleFilterChange("dateFrom", e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={e => handleFilterChange("dateTo", e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.filterField}>
                <label>SUPPLIER</label>
                <select value={filters.supplier} onChange={e => handleFilterChange("supplier", e.target.value)}>
                  <option value="all">All Suppliers</option>
                  {(Array.isArray(suppliers) ? suppliers : []).map((s) => (
                    <option key={s.supplierId || s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.filterField}>
                <label>AIRLINE</label>
                <select value={filters.airline} onChange={e => handleFilterChange("airline", e.target.value)}>
                  <option value="all">All Airlines</option>
                  {(Array.isArray(airlines) ? airlines : []).map((a) => (
                    <option key={a.id} value={a.airlineName || a.name}>
                      {a.airlineName || a.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.filterField}>
                <label>BOOKING STATUS</label>
                <select value={filters.bookingStatus} onChange={e => handleFilterChange("bookingStatus", e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div className={styles.filterField}>
                <label>FLIGHT NUMBER</label>
                <input
                  type="text"
                  value={filters.flightNumber}
                  onChange={e => handleFilterChange("flightNumber", e.target.value)}
                  placeholder="e.g., EK524"
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                />
              </div>

              <div className={styles.filterField}>
                <label>ROUTE</label>
                <input
                  type="text"
                  value={filters.route}
                  onChange={e => handleFilterChange("route", e.target.value)}
                  placeholder="e.g., DXB → LHR"
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                />
              </div>

              <div className={styles.filterField}>
                <label>BOOKING NUMBER</label>
                <input
                  type="text"
                  value={filters.bookingNumber}
                  onChange={e => handleFilterChange("bookingNumber", e.target.value)}
                  placeholder="BK-2024-001"
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                />
              </div>

              <div className={styles.filterField}>
                <label>PNR</label>
                <input
                  type="text"
                  value={filters.pnr}
                  onChange={e => handleFilterChange("pnr", e.target.value)}
                  placeholder="ABC123"
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                />
              </div>
            </div>

            {/* Search Button Row */}
            <div className={styles.filtersFooter}>
              <button className={styles.searchBtn} onClick={handleSearch} disabled={searchLoading}>
                <Search size={17} />
                {searchLoading ? "Searching…" : "Search Flights"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Search Error */}
      {searchError && (
        <div className={styles.errorBanner}>
          {searchError}
        </div>
      )}

      {/* Results Count */}
      <div className={styles.resultsBar}>
        <div className={styles.resultsCount}>
          {searchLoading
            ? "Searching…"
            : total === 0
              ? "Showing 0 flights"
              : `Showing ${rangeFrom}–${rangeTo} of ${total} flight${total === 1 ? "" : "s"}`}
          {activeTab === "api" && changesCount > 0 && (
            <span className={styles.changesIndicator}>
              • {changesCount} with schedule changes
            </span>
          )}
        </div>
        {selectedFlights.length > 0 && (
          <span className={styles.selectedCount}>{selectedFlights.length} selected</span>
        )}
      </div>

      {/* Flights Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={pagedFlights.length > 0 && pagedFlights.every((f) => selectedFlights.includes(f.id))}
                  onChange={handleSelectAll}
                />
              </th>
              <th>BOOKING</th>
              <th>FLIGHT</th>
              <th>ROUTE</th>
              <th>SCHEDULE</th>
              {/* The old "MANUAL INPUT" column held two <input type="time">
                  fields with no onChange, no state and no save — typing in them
                  did nothing, and they were too narrow to render the value.
                  Editing happens in the Manual Schedule Change modal, which
                  captures the mandatory reason, previews impact and audits. */}
              {/* §63 lists 18 columns. Eighteen is not readable — the rest live
                  in the flight detail panel (§64). These are the ones an
                  operator needs at a glance to act on a row. */}
              <th>SUPPLIER</th>
              <th>PAX</th>
              <th>CUSTOMER</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {filteredFlights.length === 0 ? (
              <tr>
                <td colSpan="9" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>✈️</div>
                    <p>{searchLoading ? "Loading flights…" : "No flights found"}</p>
                    <span>{searchLoading ? "Please wait" : "Adjust your filters and click Search Flights"}</span>
                  </div>
                </td>
              </tr>
            ) : (
              pagedFlights.map(flight => {
                // An overnight leg lands on a later calendar day. Printing
                // "00:50 → 06:00" without saying so has cost people a night.
                const overnight =
                  flight.arrivalDate && flight.departureDate && flight.arrivalDate > flight.departureDate;
                const dayShift = overnight
                  ? Math.round(
                      (Date.parse(`${flight.arrivalDate}T00:00:00Z`) -
                        Date.parse(`${flight.departureDate}T00:00:00Z`)) / 86400000
                    )
                  : 0;
                const selected = selectedFlights.includes(flight.id);
                return (
                <tr
                  key={flight.id}
                  className={[
                    flight.hasScheduleChange ? styles.highlightRow : "",
                    selected ? styles.selectedRow : "",
                  ].filter(Boolean).join(" ")}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleSelectFlight(flight.id)}
                    />
                  </td>

                  {/* Booking — reference, PNR and who it is for. */}
                  <td>
                    <div className={styles.bookingCell}>
                      {/* The operator's next move after spotting a schedule change is
                          almost always "open that booking". A real anchor, not a button,
                          so ctrl/middle-click opens it in a new tab and the list keeps
                          its filters and selection. Routed by numeric id: /bookings/<ref>
                          resolves to demo data, not the real booking. */}
                      {flight.bookingId ? (
                        <Link
                          to={`/bookings/${flight.bookingId}`}
                          className={styles.bookingLink}
                          title={`Open booking ${flight.bookingNumber}`}
                        >
                          {flight.bookingNumber}
                        </Link>
                      ) : (
                        <strong>{flight.bookingNumber}</strong>
                      )}
                      {flight.pnr && <span className={styles.pnrTag}>{flight.pnr}</span>}
                      {flight.customerName && (
                        <span className={styles.customerName}>{flight.customerName}</span>
                      )}
                    </div>
                  </td>

                  {/* Flight — real airline logo where we have one. */}
                  <td>
                    <div className={styles.flightCell}>
                      {flight.airlineLogo ? (
                        <img src={flight.airlineLogo} alt="" className={styles.airlineLogo} />
                      ) : (
                        <div className={styles.airlineIcon}><Plane size={16} /></div>
                      )}
                      <div className={styles.flightInfo}>
                        <strong>{flight.flightNumber}</strong>
                        <span>{flight.airline}</span>
                        <div className={styles.chipRow}>
                          {flight.direction && (
                            <span className={styles.chipDirection}>{flight.direction}</span>
                          )}
                          {flight.cabinClass && (
                            <span className={styles.chip}>{flight.cabinClass.replace(/ Class$/, "")}</span>
                          )}
                          {flight.manualOverride && (
                            <span className={styles.chipOverride} title={flight.manualOverrideReason}>
                              hand-corrected
                            </span>
                          )}
                        </div>
                        {flight.affectedBookings > 1 && (
                          <button
                            type="button"
                            className={styles.affectedChip}
                            title="Find all bookings on this flight"
                            onClick={() => setOperationalKey(flight.operationalFlightKey)}
                          >
                            <Users size={11} />
                            {flight.affectedBookings} bookings on this flight
                          </button>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Route — codes stay primary, cities answer "where is that?" */}
                  <td>
                    <div className={styles.routeCell}>
                      <div className={styles.routeCodes}>
                        <span className={styles.airportCode}>{flight.departureAirport || "—"}</span>
                        <MapPin size={12} className={styles.routeArrowIcon} />
                        <span className={styles.airportCode}>{flight.arrivalAirport || "—"}</span>
                      </div>
                      {(flight.departureCity || flight.arrivalCity) && (
                        <span className={styles.routeCities}>
                          {flight.departureCity || flight.departureAirport} → {flight.arrivalCity || flight.arrivalAirport}
                        </span>
                      )}
                      {flight.flightType && (
                        <span className={styles.routeType}>{flight.flightType}</span>
                      )}
                    </div>
                  </td>

                  {/* Schedule — the times, with the overnight roll made explicit. */}
                  <td>
                    <div className={styles.scheduleCell}>
                      <Calendar size={14} className={styles.scheduleIcon} />
                      <div className={styles.scheduleInfo}>
                        <span className={styles.date}>{formatDMY(flight.departureDate)}</span>
                        <span className={styles.timeRow}>
                          <span className={styles.time}>{flight.departureTime || "—"}</span>
                          <span className={styles.timeArrow}>→</span>
                          <span className={styles.time}>{flight.arrivalTime || "—"}</span>
                          {dayShift > 0 && (
                            <span className={styles.nextDay} title={`Arrives ${formatDMY(flight.arrivalDate)}`}>
                              +{dayShift}
                            </span>
                          )}
                        </span>
                        {flight.departureTerminal || flight.arrivalTerminal ? (
                          <span className={styles.terminals}>
                            {flight.departureTerminal ? `T${flight.departureTerminal}` : "—"}
                            {" → "}
                            {flight.arrivalTerminal ? `T${flight.arrivalTerminal}` : "—"}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </td>

                  {/* §61 — supplier and its reference belong together. */}
                  <td>
                    <div className={styles.supplierCell}>
                      <span>{flight.supplier || "—"}</span>
                      {flight.supplierReference && (
                        <span className={styles.supplierRef}>{flight.supplierReference}</span>
                      )}
                      <span className={flight.source === "API" ? styles.sourceApi : styles.sourceManual}>
                        {flight.source === "API" ? "Airtuerk file" : "no supplier file"}
                      </span>
                    </div>
                  </td>

                  <td><span className={styles.paxCell}>{flight.travelers || 0}</span></td>

                  {/* §49 — blank is not "no", so an untouched flight shows a dash. */}
                  <td>
                    {!flight.hasScheduleChange ? (
                      <span className={styles.muted}>—</span>
                    ) : flight.lastChange?.customerNotified ? (
                      <span className={styles.notifiedYes}>Notified</span>
                    ) : (
                      /* The badge IS the action — telling the operator the
                         customer has not been told and making them hunt for
                         where to do it is the gap this row is complaining about. */
                      <button
                        type="button"
                        className={styles.notifyBtn}
                        disabled={notifyLoading}
                        title="Email the lead booker about this schedule change"
                        onClick={() => handleNotify(flight)}
                      >
                        {notifyLoading ? "Sending…" : "Notify customer"}
                      </button>
                    )}
                  </td>

                  <td>
                    {flight.hasScheduleChange ? (
                      <span className={`${styles.statusBadge} ${styles.statusWarning}`}>
                        <AlertTriangle size={14} />
                        Schedule Change
                      </span>
                    ) : flight.cancelled || flight.bookingStatus === "Cancelled" ? (
                      <span className={`${styles.statusBadge} ${styles.statusCancelled}`}>
                        <AlertTriangle size={14} />
                        Cancelled
                      </span>
                    ) : (
                      <span className={`${styles.statusBadge} ${styles.statusConfirmed}`}>
                        <CheckCircle size={14} />
                        {flight.bookingStatus}
                      </span>
                    )}
                  </td>
                </tr>
                );
              })

            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — rows per page sits with the pager rather than up in the
          filter panel, because it is about reading the result, not narrowing it. */}
      {!searchLoading && total > 0 && (
        <div className={styles.pagination}>
          <div className={styles.rowsPer}>
            <span>Rows per page:</span>
            <select
              className={styles.rowsSelect}
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className={styles.pager}>
            <button type="button" className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
            <button type="button" className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>‹</button>
            {pageNumbers.map((n) => (
              <button
                key={n}
                type="button"
                className={`${styles.pageBtn} ${n === safePage ? styles.pageBtnActive : ""}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button type="button" className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(safePage + 1)}>›</button>
            <button type="button" className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(pageCount)}>»</button>
          </div>

          <div className={styles.showingText}>
            Page {safePage} of {pageCount} · {selectedFlights.length} selected
          </div>
        </div>
      )}

      <OperationalFlightModal
        open={Boolean(operationalKey)}
        onClose={() => setOperationalKey(null)}
        operationalFlightKey={operationalKey}
        onApplyToAll={handleApplyToWholeFlight}
      />

      <ManualScheduleChangeModal
        open={scheduleModalOpen}
        onClose={() => { setScheduleModalOpen(false); setFlightWideSegments(null); }}
        segments={applyTargets}
        onApply={handleApplyChanges}
        applying={applyLoading}
      />
    </div>
  );
};

export default ScheduleUpdater;